import {
  FlowItem,
  FlowItemParameterMetadata,
  ParametersForm,
} from '../../../shared/model/flow.types';
import { FlowItemService } from '../../../shared/service/service.types';
import {
  getParameterValueFromForm,
  getParametersFormWaitForTags,
} from '../../../shared/util/flow-parameter.util';
import { BeaconCuttingData } from '../model/beacon-cutting-data';

const isSingleItemCut = (
  cutType: BeaconCuttingData.CutTypeEnum | undefined
) => {
  if (cutType !== undefined) {
    return [BeaconCuttingData.CutTypeEnum.ROTARY].includes(cutType);
  }

  return false;
};

const isCutType = (
  flowItem: FlowItem,
  type: BeaconCuttingData.CutTypeEnum
): boolean => {
  if (flowItem.ref !== BeaconCuttingData.FlowItemRef.CUT) {
    return false;
  }

  const idx = flowItem.parameters.findIndex(
    (p) =>
      p.ref === BeaconCuttingData.FlowItemParameterRef.CUT_TYPE &&
      p.value === type
  );

  return idx !== -1;
};

const getCuttingParameterMetadataFromForm = (
  flowItemService: FlowItemService,
  form: ParametersForm,
  options: BeaconCuttingData.GetParameterMetadataFromFormOptions = {}
): FlowItemParameterMetadata[] => {
  const {
    forceDepart,
    forceApproach,
    forceIncludeTags,
    forceExcludeRefs,
    forceCutType,
  } = options;

  let tags: BeaconCuttingData.FlowItemParameterTag[] = [];
  let excludedParams = [...(forceExcludeRefs ?? [])];

  const isDepart =
    forceDepart !== undefined
      ? forceDepart
      : getParameterValueFromForm<boolean>(
          form,
          BeaconCuttingData.FlowItemParameterRef.DEPART
        );

  const isApproach =
    forceApproach !== undefined
      ? forceApproach
      : getParameterValueFromForm<boolean>(
          form,
          BeaconCuttingData.FlowItemParameterRef.APPROACH
        );

  const cutType =
    forceCutType !== undefined
      ? forceCutType
      : getParameterValueFromForm<BeaconCuttingData.CutTypeEnum>(
          form,
          BeaconCuttingData.FlowItemParameterRef.CUT_TYPE
        );

  const singleItemCut = isSingleItemCut(cutType);

  const isRotaryCut = cutType === BeaconCuttingData.CutTypeEnum.ROTARY;

  const process = getParameterValueFromForm(
    form,
    BeaconCuttingData.FlowItemParameterRef.PROCESS
  ) as BeaconCuttingData.CutProcessValue;

  if (isDepart || singleItemCut) {
    tags.push(BeaconCuttingData.FlowItemParameterTag.DEPART);
  }

  if (isApproach || singleItemCut) {
    tags.push(BeaconCuttingData.FlowItemParameterTag.APPROACH);
  }

  if (isRotaryCut) {
    tags.push(BeaconCuttingData.FlowItemParameterTag.ROTARY);
    tags.push(
      ...(getParametersFormWaitForTags(
        form
      ) as BeaconCuttingData.FlowItemParameterTag[])
    );
  }

  if (!isDepart || isRotaryCut) {
    tags.push(process as unknown as BeaconCuttingData.FlowItemParameterTag);
  }

  if (forceIncludeTags) {
    tags = tags.concat(forceIncludeTags);
  }

  const uniqueTags = Array.from(new Set(tags));

  let params = flowItemService.getParametersMetadataByTags(
    BeaconCuttingData.FlowItemRef.CUT,
    form,
    uniqueTags as string[],
    true
  );

  if (isRotaryCut) {
    excludedParams = excludedParams.concat([
      BeaconCuttingData.FlowItemParameterRef.TRAVEL_SPEED,
    ]);
  } else if (isDepart) {
    excludedParams = excludedParams.concat([
      BeaconCuttingData.FlowItemParameterRef.TRAVEL_SPEED,
    ]);
  }

  params = params.filter(
    (param) =>
      !excludedParams.includes(
        param.ref as BeaconCuttingData.FlowItemParameterRef
      )
  );

  return params;
};

export { getCuttingParameterMetadataFromForm, isCutType };
